import Css from "./style.module.scss";

import { checkMatchesFetching, getGlobalStats } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { getUserData } from "selectors/user";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import DataStatuses from "const/DataStatuses";
import DocumentFileTypes from "const/DocumentFileTypes";
import DocumentsStatusFilter from "./lib/DocumentsStatusFilter";
import DocumentsTable from "./lib/DocumentsTable";
import EditDocument from "nlib/common/EditDocument";
import HeaderButtons from "./lib/HeaderButtons";
import MonthsTabs from "nlib/common/MonthsTabs";
import Page from "nlib/common/Page";
import PageContent from "nlib/common/PageContent";
import PageHeader from "nlib/common/PageHeader";
import React, { useCallback, useMemo, useRef, useState } from "react";
import Sidebar from "./lib/Sidebar";
import Texts from "utils/Texts";
import classNames from "classnames";
import useAvailableWidth from "hooks/useAvailableWidth";
import useDocumentUtils from "hooks/useDocumentUtils";
import useDocuments from "hooks/useDocuments";
import useEnvVars from "hooks/useEnvVars";
import useUploadDocuments from "hooks/useUploadDocuments";

const SIMPLIFIED_LAYOUT_MAX_WIDTH = 1020;

const FILE_DROP_ALLOWED_TYPES = Object.values(DocumentFileTypes)
  .reduce((aggregator, { extensions, mimeType }) => [...aggregator, ...extensions, mimeType], []);

const DocumentsPage = () => {
  const contentRef = useRef();

  const { messages } = useSelector(getTextsData);

  const [envVars, setEnvVars] = useEnvVars();

  const [dragOver, setDragOver] = useState(false);

  const [editDocumentId] = envVars.editItem ? envVars.editItem.split(".") : [];

  const matchesFetching = useSelector(checkMatchesFetching);

  const userData = useSelector(getUserData);

  const globalStats = useSelector(getGlobalStats);

  const { openPairedTransactionsWindow } = useDocumentUtils();

  const {
    fetching: documentsFetching,
    data: documentsData,
    refetch: refetchDocuments
  } = useDocuments();

  const { containerRef, availableWidth } = useAvailableWidth([documentsFetching, envVars.editTask, envVars.editItem]);

  const uploadDocuments = useUploadDocuments();

  const [uploadDocumentsWindowOpened, setUploadDocumentsWindowOpened] = useState(false);

  const simplifyLayout = availableWidth <= SIMPLIFIED_LAYOUT_MAX_WIDTH;

  const fetchingData = documentsFetching || matchesFetching;

  const extractedDocumentsData = documentsData.filter(({ status }) => status !== DataStatuses.TO_EXTRACT);

  const filtersEnabled = [
    envVars.text, envVars.fromDate, envVars.toDate, envVars.accountId, envVars.type
  ].find(Boolean);

  const byMonthData = useMemo(() => {
    return [
      DataStatuses.TO_REVIEW,
      DataStatuses.EXCLUDED,
      DataStatuses.TO_REPORT,
      DataStatuses.EXPORTED
    ].reduce((result, status) => {
      const sectionData = (globalStats.documents || {})[status] || {};

      if (sectionData.byMonth) {
        Object.entries(sectionData.byMonth).forEach(([month, { count, comments }]) => {
          const { count: prevCount = 0, comments: prevComments = 0 } = result[month] || {};

          result[month] = { count: count + prevCount, comments: comments + prevComments };
        });
      }

      return result;
    }, {});
  }, [globalStats.documents]);

  const openDocument = useCallback((id) => {
    setEnvVars({ editTask: null });

    const [documentId] = (id || "").split(".");

    const documentData = Arrays.findById(documentsData, documentId);

    if (!documentData || documentData.status === DataStatuses.TO_EXTRACT) return;
    setEnvVars({ editItem: id });
  }, [documentsData, setEnvVars]);

  const handlePairTransactionsLinkClick = useCallback(async(id) => {
    const documentData = Arrays.findById(documentsData, id);

    if (documentData) {
      const result = await openPairedTransactionsWindow(documentData);

      if (result) refetchDocuments();
    }
  }, [documentsData, openPairedTransactionsWindow, refetchDocuments]);

  const handleEditDocumentWindowClose = useCallback((refetchData) => {
    setEnvVars({ editItem: null });
    if (refetchData) refetchDocuments(true);
  }, [refetchDocuments, setEnvVars]);

  const handleStatusTabClick = useCallback((value) => {
    if (envVars.status === value) refetchDocuments();
  }, [envVars.status, refetchDocuments]);

  const handleMonthTabChange = useCallback((params) => {
    if (params.fromDate !== envVars.fromDate || params.toDate !== envVars.toDate) {
      setEnvVars({ ...params, text: null, type: null, accountId: null });
    } else {
      refetchDocuments();
    }
  }, [setEnvVars, envVars.fromDate, envVars.toDate, refetchDocuments]);

  const handleUploadDocumentsOpen = useCallback(() => {
    setUploadDocumentsWindowOpened(true);
  }, []);

  const handleSideBarClose = useCallback(async(result) => {
    setUploadDocumentsWindowOpened(false);
    if (!result) return;

    const response = await uploadDocuments(result);

    if (response) refetchDocuments();
  }, [refetchDocuments, uploadDocuments]);

  const handleDragEnter = useCallback((event) => {
    setDragOver(true);
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(async(event) => {
    event.preventDefault();
    setDragOver(false);

    const acceptedFiles = [...event.dataTransfer.files]
      .filter((file) => FILE_DROP_ALLOWED_TYPES.some((ext) => file.name.endsWith(ext)));

    if (!acceptedFiles.length) return;
    toast.info(acceptedFiles.length > 1
      ? Texts.replaceVars(messages.filesAddedToUploadQueue, { count: acceptedFiles.length })
      : messages.fileAddedToUploadQueue);

    const response = await uploadDocuments({ files: acceptedFiles });

    if (response) refetchDocuments();
  }, [refetchDocuments, uploadDocuments, messages]);

  return (
    <>
      <Page className={Css.documentsPage} pageRef={containerRef} onDragEnter={handleDragEnter}>
        <PageContent ref={contentRef}>
          <PageHeader>
            {(!!documentsData.length || (!envVars.status || filtersEnabled)) && (
              <HeaderButtons
                disabled={fetchingData}
                hideUploadButton={uploadDocumentsWindowOpened}
                onUploadDocumentsOpen={handleUploadDocumentsOpen} />
            )}
          </PageHeader>
          {!userData.guestUser && (
            <MonthsTabs
              disabled={fetchingData}
              data={byMonthData}
              value={`${envVars.fromDate}-${envVars.toDate}`}
              onChange={handleMonthTabChange} />
          )}
          <DocumentsStatusFilter
            disabled={fetchingData}
            simplifyLayout={simplifyLayout}
            onClick={handleStatusTabClick} />
          <DocumentsTable
            fetchingData={fetchingData}
            simplifyLayout={simplifyLayout}
            documentsData={documentsData}
            uploadDocumentsWindowOpened={uploadDocumentsWindowOpened}
            openDocument={openDocument}
            refetchDocuments={refetchDocuments}
            onUploadDocumentsOpen={handleUploadDocumentsOpen}
            onPairTransactionsLinkClick={handlePairTransactionsLinkClick} />
        </PageContent>
        <div
          className={classNames(Css.overlay, dragOver && Css.dragOver)}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop} />
      </Page>
      {!!editDocumentId && (
        <EditDocument
          key={editDocumentId}
          documentId={editDocumentId}
          extractedDocumentsData={extractedDocumentsData}
          openDocument={openDocument}
          refetchDocuments={refetchDocuments}
          onClose={handleEditDocumentWindowClose} />
      )}
      {uploadDocumentsWindowOpened && (
        <Sidebar onClose={handleSideBarClose} />
      )}
    </>
  );
};

export default React.memo(DocumentsPage);
